"use client";

import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { Alert, Button } from "../../../components/ui";
import { useDialogFocusTrap } from "../../../components/ui/useDialogFocusTrap";
import { BookingCodeLogin } from "./BookingCodeLogin";

export type BookingDecisionSummary = {
  name: string;
  membershipPlanName?: string;
  message: string;
  amountLabel: string;
  confirmLabel?: string;
  adjustLabel?: string;
  services: Array<{ id: string; name: string; label: string; amountLabel?: string }>;
};

interface Props {
  initialEmail: string;
  priceLabel: string;
  hasProducts: boolean;
  busy: boolean;
  submitting?: boolean;
  error: string | null;
  notice?: string | null;
  summary: BookingDecisionSummary | null;
  onClose: () => void;
  onLogin: (token: string) => Promise<void>;
  onConfirm: () => void;
  onRetry: () => void;
  onAdjust?: () => void;
  canChooseGuest: boolean;
  hasMembershipMatch: boolean;
}

export function BookingDecisionDialog(props: Props) {
  const [login, setLogin] = useState(false);
  const panelRef = useDialogFocusTrap<HTMLDivElement>(true, "container");
  const { submitting, onClose } = props;
  const ready = props.canChooseGuest || Boolean(props.summary);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [submitting, onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/50 p-3 sm:p-6"
      onClick={(event) => {
        if (event.target === event.currentTarget && !props.submitting) props.onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        data-pwa-install-blocker="true"
        aria-labelledby="booking-decision-title"
        aria-describedby="booking-decision-description"
        tabIndex={-1}
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200/80 bg-white p-4 shadow-subtle outline-none md:rounded-3xl md:p-6"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id="booking-decision-title" className="text-xl font-bold text-slate-900">
            {props.summary
              ? "Confira seu agendamento"
              : ready
                ? "Antes de confirmar"
                : "Verificando agendamento"}
          </h2>
          <button
            type="button"
            aria-label="Fechar confirmação"
            disabled={props.submitting}
            onClick={props.onClose}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 hover:bg-slate-50 focus-visible:ring-4 focus-visible:ring-primary-100 disabled:opacity-50"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <p
          id="booking-decision-description"
          className="mt-3 text-sm leading-6 text-slate-600"
        >
          {!ready
            ? "Aguarde a verificação antes de confirmar seu atendimento."
            : props.summary
              ? `Você está conectado como ${props.summary.name}.`
              : props.hasMembershipMatch && !props.notice && !props.error
                ? "Encontramos uma assinatura neste estabelecimento para os dados informados. Entre na sua conta para conferir a cobertura deste atendimento."
                : "Entre na sua conta para conferir a cobertura deste atendimento."}
        </p>
        {props.error && (
          <div className="mt-3">
            <Alert tone="error" mirrorToast={false}>
              {props.error}
            </Alert>
          </div>
        )}
        {props.notice && (
          <div className="mt-3">
            <Alert tone="warning" mirrorToast={false}>
              {props.notice}
            </Alert>
          </div>
        )}
        {props.busy && (
          <p role="status" className="mt-3 text-sm text-slate-500">
            Verificando seu agendamento...
          </p>
        )}
        {props.summary ? (
          <div className="mt-4 space-y-3 text-sm text-slate-700">
            {props.summary.membershipPlanName && (
              <p className="break-words">
                <span className="font-medium">Sua assinatura: </span>
                {props.summary.membershipPlanName}
              </p>
            )}
            <p>{props.summary.message}</p>
            <ul
              aria-label="Serviços do agendamento"
              className="divide-y divide-slate-200/80"
            >
              {props.summary.services.map((service) => (
                <li key={service.id} className="flex min-w-0 flex-col gap-1 py-2">
                  <span className="font-medium">{service.name}</span>
                  <span className="text-slate-500">{service.label}</span>
                  {service.amountLabel && (
                    <span className="text-slate-700">{service.amountLabel}</span>
                  )}
                </li>
              ))}
            </ul>
            <p className="font-semibold">{props.summary.amountLabel}</p>
          </div>
        ) : ready ? (
          <div className="mt-4 space-y-2 text-sm text-slate-700">
            <p>
              Como visitante, este agendamento será avulso e não usará os benefícios da
              assinatura.
            </p>
            <p className="font-semibold">{props.priceLabel}</p>
          </div>
        ) : null}
        {props.hasProducts && ready && (
          <p className="mt-3 text-xs leading-5 text-slate-500">
            Os produtos reservados serão conferidos e pagos no atendimento. O total acima
            inclui somente os serviços.
          </p>
        )}
        {login && !props.summary && (
          <div className="mt-4 border-t border-slate-200/80 pt-4">
            <BookingCodeLogin
              initialEmail={props.initialEmail}
              onSuccess={props.onLogin}
              disabled={props.busy}
            />
          </div>
        )}
        <div className="mt-5 grid gap-2">
          {!props.summary && ready && (
            <Button type="button" disabled={props.busy} onClick={() => setLogin(true)}>
              Entrar na minha conta
            </Button>
          )}
          {(props.error || props.notice) && (
            <Button
              type="button"
              variant="outline"
              disabled={props.busy}
              onClick={props.onRetry}
            >
              Tentar novamente
            </Button>
          )}
          {(props.summary || props.canChooseGuest) && (
            <Button
              type="button"
              variant={props.summary ? "primary" : "outline"}
              disabled={props.busy || Boolean(props.error)}
              onClick={props.onConfirm}
            >
              {props.summary
                ? props.summary.confirmLabel || "Confirmar agendamento"
                : "Confirmar como visitante"}
            </Button>
          )}
          {props.summary?.adjustLabel && props.onAdjust && (
            <Button
              type="button"
              variant="outline"
              disabled={props.busy}
              onClick={props.onAdjust}
            >
              {props.summary.adjustLabel}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            disabled={props.submitting}
            onClick={props.onClose}
          >
            Voltar aos dados
          </Button>
        </div>
      </div>
    </div>
  );
}
